/**
 * Port of `EntryScreen.swift` from the Mac app — the ONE implementation of the built-in entry
 * rule (dividend yield within bounds AND price near its 52-week low).
 *
 * This file is deliberately a close translation rather than an idiomatic rewrite. The demo's
 * whole claim is "these are the same rules the real app runs", so the two must stay comparable
 * by eye. The test file mirrors the Swift test cases one-for-one for the same reason.
 *
 * Two properties carried over from the Swift original that are easy to lose in translation:
 *
 *   1. It FAILS CLOSED. A missing yield or a missing distance-to-low is never treated as
 *      satisfied. In the Swift app this mattered because unknown yield silently passing a
 *      yield-gated screen produced false "Buy" signals on stocks that pay no dividend at all.
 *
 *   2. Yield is derived as annual dividend per share ÷ price, with NO scale guessing. The
 *      original bug it fixed: a provider field that was already a percentage (2.3 meaning 2.3%)
 *      was run through `dy > 1 ? dy/100 : dy`, so a 0.7% yield stayed 0.72 and was read as 72%.
 *      Every sub-1% payer became a false match. Deriving from rate ÷ price is unambiguous.
 */

import type { Verdict } from './types';

/**
 * The single entry-screen evaluation.
 *
 * `pctAboveLowFraction` and `yieldFraction` are both FRACTIONS (0.09 = 9% above the low,
 * 0.048 = 4.8% yield). Thresholds are passed as primitives rather than a config object so
 * that every caller shares this one function without constructing the others' types — same
 * reasoning as the Swift version.
 */
export function evaluate(params: {
  pctAboveLowFraction: number | null;
  yieldFraction: number | null;
  enabled: boolean;
  nearLowThresholdFraction: number;
  minYieldFraction: number;
  maxYieldFraction: number;
}): Verdict {
  const {
    pctAboveLowFraction, yieldFraction, enabled,
    nearLowThresholdFraction, minYieldFraction, maxYieldFraction,
  } = params;

  if (!enabled) return 'doesNotQualify';
  // Fail closed on unknown inputs — an unverifiable condition is never "satisfied".
  if (pctAboveLowFraction === null || yieldFraction === null) return 'doesNotQualify';

  if (pctAboveLowFraction < 0) return 'doesNotQualify';
  if (pctAboveLowFraction > nearLowThresholdFraction) return 'doesNotQualify';
  if (yieldFraction < minYieldFraction) return 'doesNotQualify';

  return yieldFraction > maxYieldFraction ? 'highYieldFlagged' : 'qualifies';
}

/** True only for a CLEAN qualifying hit — a flagged over-ceiling hit is not a clean qualifier. */
export function qualifies(params: Parameters<typeof evaluate>[0]): boolean {
  return evaluate(params) === 'qualifies';
}

/**
 * The single, deterministic way to turn dividend data into a yield fraction.
 * Returns null when it cannot be known — callers must treat that as "unknown", never as zero.
 */
export function dividendYieldFraction(
  annualRate: number | null,
  price: number | null,
): number | null {
  if (annualRate !== null && annualRate > 0 && price !== null && price > 0) {
    return annualRate / price;
  }
  return null;
}

/** (price − low) / low. Null when the low isn't known, so the screen fails closed. */
export function pctAboveLow(price: number | null, low: number | null): number | null {
  if (price === null || low === null || low <= 0) return null;
  return (price - low) / low;
}
